import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { LeadListItem, Role } from '../types';
import { formatDateTime } from '../leadLabels';
import { LeadDetail } from './LeadDetail';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { SortableHeader, type SortDirection } from '../components/SortableHeader';

const PAGE_SIZE = 50;

/** Hebrew label for how the opt-out was recorded. */
function optOutSourceLabel(source?: string): string {
  if (source === 'auto') return 'אוטומטי (הודעת לקוח)';
  if (source === 'agent') return 'ידני (נציג)';
  return '—';
}

const SOURCE_OPTIONS = [
  { value: 'auto', label: 'אוטומטי (הודעת לקוח)' },
  { value: 'agent', label: 'ידני (נציג)' },
];

/**
 * Dedicated "removal list" screen: every lead that opted out of the mailing
 * list ("הסר" / "מחק"). Mirrors the Leads workspace (search, filter panel,
 * sortable + expandable grid, pagination). Sending messages to these leads is
 * blocked by the system; an agent can restore a lead only when the customer
 * re-consents.
 */
export function OptOutList({ tenantId, role }: { tenantId?: string; role: Role }) {
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState('');

  // Search (server-side, debounced).
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Filters (client-side).
  const [showFilters, setShowFilters] = useState(true);
  const [filterSources, setFilterSources] = useState<string[]>([]);
  const [removedFrom, setRemovedFrom] = useState('');
  const [removedTo, setRemovedTo] = useState('');

  // Sort + pagination + expansion.
  const [sortKey, setSortKey] = useState<string | null>('opt_out_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);

  const canEdit = role === 'Agent' || role === 'AgencyAdmin' || role === 'SystemAdmin';

  const refresh = useCallback(() => {
    setLoading(true);
    api
      .listLeads({ q: debouncedQ.trim() || undefined, stage: 'opt_out', archived: 'all' }, tenantId)
      .then(setLeads)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [debouncedQ, tenantId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  // Debounce the search box (300ms) and reset to the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [filterSources, removedFrom, removedTo, sortKey, sortDir]);

  // Apply client-side filters.
  const filtered = useMemo(() => {
    const from = removedFrom ? new Date(removedFrom).getTime() : undefined;
    const to = removedTo ? new Date(removedTo).getTime() + 86_399_999 : undefined;
    return leads.filter((l) => {
      if (filterSources.length && !(l.opt_out_source && filterSources.includes(l.opt_out_source)))
        return false;
      const t = l.opt_out_at ? new Date(l.opt_out_at).getTime() : undefined;
      if (from !== undefined && (t === undefined || t < from)) return false;
      if (to !== undefined && (t === undefined || t > to)) return false;
      return true;
    });
  }, [leads, filterSources, removedFrom, removedTo]);

  // Sort (client-side).
  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (l: LeadListItem): string | number => {
      switch (sortKey) {
        case 'customer':
          return (l.customer_name || l.phone).toLowerCase();
        case 'opt_out_at':
          return l.opt_out_at ? new Date(l.opt_out_at).getTime() : 0;
        case 'source':
          return optOutSourceLabel(l.opt_out_source);
        default:
          return '';
      }
    };
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'he') * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = sorted.slice(pageStart, pageStart + PAGE_SIZE);

  function handleSort(key: string) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') {
        setSortDir(null);
        setSortKey(null);
      } else setSortDir('asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function goToPage(p: number) {
    setPage(p);
    requestAnimationFrame(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  function toggleExpand(phone: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  }

  const hasActiveFilters = filterSources.length > 0 || removedFrom !== '' || removedTo !== '';
  const activeFilterCount = [filterSources.length > 0, removedFrom !== '', removedTo !== ''].filter(
    Boolean,
  ).length;

  function clearAllFilters() {
    setFilterSources([]);
    setRemovedFrom('');
    setRemovedTo('');
  }

  async function restore(phone: string) {
    if (
      !window.confirm(
        'להחזיר את הלקוח לרשימת התפוצה? יש לעשות זאת רק לאחר שהלקוח ביקש מפורשות להצטרף מחדש.',
      )
    )
      return;
    try {
      await api.setOptOut(phone, false, tenantId);
      setNote('הלקוח הוחזר לרשימת התפוצה.');
      refresh();
    } catch {
      setNote('הפעולה נכשלה.');
    }
  }

  function exportCsv() {
    const headers = ['שם לקוח', 'טלפון', 'תאריך הסרה', 'מקור', 'הודעה מזוהה'];
    const rows = sorted.map((l) => [
      l.customer_name || '',
      l.phone,
      l.opt_out_at || '',
      optOutSourceLabel(l.opt_out_source),
      l.opt_out_text || '',
    ]);
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opt-out-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (selected) {
    return (
      <LeadDetail
        phone={selected}
        tenantId={tenantId}
        role={role}
        onBack={() => {
          setSelected(null);
          refresh();
        }}
      />
    );
  }

  // Compact page-number list with ellipses.
  const pageNumbers: (number | 'gap')[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1) pageNumbers.push(p);
    else if (pageNumbers[pageNumbers.length - 1] !== 'gap') pageNumbers.push('gap');
  }

  const Pagination = () =>
    totalPages > 1 ? (
      <div className="results-bar">
        <span>
          מציג {sorted.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, sorted.length)} מתוך{' '}
          {sorted.length.toLocaleString('he-IL')}
        </span>
        <div className="spacer" />
        <div className="pager">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
            הקודם
          </button>
          {pageNumbers.map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} className="ellipsis">…</span>
            ) : (
              <button key={p} className={p === currentPage ? 'active' : ''} onClick={() => goToPage(p)}>
                {p}
              </button>
            ),
          )}
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}>
            הבא
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
        </div>
      </div>
    ) : (
      <div className="results-bar">
        <span>{sorted.length.toLocaleString('he-IL')} לקוחות ברשימת ההסרה</span>
      </div>
    );

  return (
    <div className="leads">
      <div className="leads-header">
        <div>
          <h1 className="page-title">רשימת הסרה</h1>
          <p className="page-subtitle">
            לקוחות שביקשו להסירם מרשימת התפוצה ("הסר"). שליחת הודעות אליהם חסומה במערכת.
          </p>
        </div>
        <div className="leads-actions">
          <button className="btn outline" onClick={exportCsv}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            ייצוא לאקסל
          </button>
          <button className="btn" onClick={refresh} disabled={loading}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3L21 8M21 3v5h-5M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3L3 16M3 21v-5h5" /></svg>
            {loading ? 'מרענן…' : 'רענון'}
          </button>
        </div>
      </div>

      {note && <div className="leads-note">{note}</div>}

      <div className="leads-card">
        <div className="leads-card-header">
          <div className="search-and-tools">
            <div className="search-box">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                className="leads-search"
                value={q}
                placeholder="חיפוש לפי טלפון, שם לקוח…"
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="leads-actions">
              <button
                className={`btn ${showFilters ? '' : 'outline'}`}
                onClick={() => setShowFilters((s) => !s)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
                פילטרים
                {hasActiveFilters && <span className="count-badge">{activeFilterCount}</span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filters-line">
                <div className="filter-group">
                  <span className="flabel">מקור הסרה:</span>
                  <MultiSelectFilter
                    options={SOURCE_OPTIONS}
                    selected={filterSources}
                    onChange={setFilterSources}
                    searchPlaceholder="חפש מקור…"
                  />
                </div>
              </div>

              <div className="filters-line dates">
                <div className="filter-group">
                  <svg className="cal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                  <span className="flabel">תאריך הסרה:</span>
                  <input className="date-input" type="date" value={removedFrom} onChange={(e) => setRemovedFrom(e.target.value)} />
                  <span className="date-sep">עד</span>
                  <input className="date-input" type="date" value={removedTo} onChange={(e) => setRemovedTo(e.target.value)} />
                </div>
                {hasActiveFilters && (
                  <button className="btn danger clear-all" onClick={clearAllFilters}>
                    נקה הכל
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="leads-card-body optout-grid" ref={tableRef}>
          <Pagination />

          {pageItems.length === 0 ? (
            <div className="empty">
              {hasActiveFilters || debouncedQ
                ? 'לא נמצאו לקוחות התואמים את הפילטרים'
                : 'אין לקוחות ברשימת ההסרה'}
            </div>
          ) : (
            <>
              <div className="lead-grid-header">
                <SortableHeader label="לקוח" sortKey="customer" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="תאריך הסרה" sortKey="opt_out_at" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="מקור" sortKey="source" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <span className="sortable">הודעה מזוהה</span>
                <span className="sortable">סטטוס שליחה</span>
                <span className="sortable">פעולות</span>
                <span />
              </div>

              <div className="lead-list">
                {pageItems.map((l) => (
                  <OptOutRow
                    key={l.phone}
                    lead={l}
                    expanded={expanded.has(l.phone)}
                    canEdit={canEdit}
                    onToggleExpand={() => toggleExpand(l.phone)}
                    onOpen={() => setSelected(l.phone)}
                    onRestore={() => restore(l.phone)}
                  />
                ))}
              </div>
            </>
          )}

          <Pagination />
        </div>
      </div>
    </div>
  );
}

/** A single expandable opt-out row (desktop grid / mobile card via CSS). */
function OptOutRow({
  lead,
  expanded,
  canEdit,
  onToggleExpand,
  onOpen,
  onRestore,
}: {
  lead: LeadListItem;
  expanded: boolean;
  canEdit: boolean;
  onToggleExpand: () => void;
  onOpen: () => void;
  onRestore: () => void;
}) {
  return (
    <div className={`lead-row${expanded ? ' expanded' : ''}`}>
      <div className="lead-row-main" onClick={onToggleExpand}>
        {/* לקוח */}
        <div className="lead-cell">
          <span className="cell-microlabel">לקוח</span>
          <div className="cell-value">
            <button
              type="button"
              className="link-phone"
              title="פתיחת כרטיס ליד"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              {lead.customer_name || lead.phone}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
            </button>
            {lead.customer_name && <div className="cell-sub">{lead.phone}</div>}
          </div>
        </div>

        {/* תאריך הסרה */}
        <div className="lead-cell">
          <span className="cell-microlabel">תאריך הסרה</span>
          <div className="cell-value"><span className="chip">{formatDateTime(lead.opt_out_at)}</span></div>
        </div>

        {/* מקור */}
        <div className="lead-cell">
          <span className="cell-microlabel">מקור</span>
          <div className="cell-value">{optOutSourceLabel(lead.opt_out_source)}</div>
        </div>

        {/* הודעה מזוהה */}
        <div className="lead-cell">
          <span className="cell-microlabel">הודעה מזוהה</span>
          <div className="cell-value truncate">{lead.opt_out_text || '—'}</div>
        </div>

        {/* סטטוס שליחה */}
        <div className="lead-cell">
          <span className="cell-microlabel">סטטוס שליחה</span>
          <div className="cell-value handling-tag">
            <span className="stage-badge heat-lost">שליחה חסומה</span>
          </div>
        </div>

        {/* פעולות */}
        <div className="lead-cell" onClick={(e) => e.stopPropagation()}>
          <span className="cell-microlabel">פעולות</span>
          <div className="cell-value">
            {canEdit ? (
              <button className="link-btn" onClick={onRestore}>
                החזר לרשימה
              </button>
            ) : (
              '—'
            )}
          </div>
        </div>

        {/* Chevron */}
        <div className="chevron-cell">
          {expanded ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          )}
        </div>
      </div>

      {expanded && (
        <div className="lead-expanded">
          <div className="lead-expanded-grid">
            <div className="lead-expanded-field">
              <span className="cell-microlabel">שם מלא</span>
              <div className="cell-value">{lead.customer_name || '—'}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">טלפון</span>
              <div className="cell-value">{lead.phone}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">תאריך הסרה</span>
              <div className="cell-value">{formatDateTime(lead.opt_out_at)}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">מקור הסרה</span>
              <div className="cell-value">{optOutSourceLabel(lead.opt_out_source)}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">הודעה מזוהה</span>
              <div className="cell-value">{lead.opt_out_text || '—'}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">נוצר</span>
              <div className="cell-value">{formatDateTime(lead.created_at)}</div>
            </div>
          </div>
          <div className="lead-expanded-actions">
            {canEdit && (
              <button
                className="btn secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore();
                }}
              >
                החזר לרשימת התפוצה
              </button>
            )}
            <button
              className="btn"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              פתיחת כרטיס ליד
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
